import { EnhancedScrollArea } from "@components/enhanced-scroll-area";
import { CoinsVal } from "@components/icons/coins";
import { useCreateImage } from "@features/images/use-create-image";
import { getPackPreview } from "@features/images/use-packs";
import { LorasType } from "@features/loras/type";
import { useLora } from "@features/loras/use-loras";
import { useHideCosts, useUser } from "@features/user/use-user";
import { useEffectPlease } from "@features/utils";
import { Button, Heading, Link, Text, Tooltip } from "@radix-ui/themes";
import { getPhotoCost } from "@views/app/camera/components/form-footer";
import posthog from "posthog-js";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

export const StepReview = () => {
  const { t } = useTranslation();
  const { id, site } = useParams();
  const { data: lora } = useLora(id || "");
  const navigate = useNavigate();
  const hideCosts = useHideCosts(); // New users should not be bothered with costs yet
  const { create } = useCreateImage();
  const { credits } = useUser();

  useEffectPlease(() => {
    if (lora?.technology === "images" && credits <= 150) {
      posthog.capture("reached_lora_review", {});
    }
  }, [lora?.technology, credits <= 150]);

  if (!lora) {
    return null;
  }

  const isNewLora =
    lora.created_at >= new Date(Date.now() - 30 * 60 * 1000).getTime();

  const packName: any = {
    professional_natural: "Natural", // DO NOT TRANSLATE THAT
    professional_black: "Black", // DO NOT TRANSLATE THAT
    professional_in_big_city: "Large City", // DO NOT TRANSLATE THAT
    outdoor_adventure: "Outdoor", // DO NOT TRANSLATE THAT
  };
  let defaultPacks = [
    "professional_natural",
    "professional_black",
    "professional_in_big_city",
    "outdoor_adventure",
  ];
  let model = "seb";

  if (lora.details.gender === "woman") {
    model = "kat";
  }

  return (
    <div className="space-y-8">
      <Heading size="6" className="mb-4">
        {lora.status === "completed"
          ? t("loras.review.model_ready")
          : t("loras.review.model_processing")}
      </Heading>

      <div className="space-y-4">
        <Heading size="4" className="mb-4">
          {t("loras.review.model_parameters")}
        </Heading>
        <ModelReviewCard lora={lora} />
        <div>
          <Link
            href="#"
            className="mt-2 mb-6 block w-max"
            onClick={() => navigate(`/${site}/lora/${lora.id}/edit`)}
          >
            {t("loras.review.change_details")}
          </Link>
        </div>
      </div>

      {(lora.status === "completed" || lora.technology === "images") && (
        <div className="space-y-4">
          <Heading size="4" className="mb-4">
            {isNewLora
              ? t("loras.review.get_first_pictures")
              : t("loras.review.generate_pictures")}
          </Heading>

          <EnhancedScrollArea className="mb-4 h-60">
            <div className="space-x-4 flex">
              {defaultPacks.map((pack) => (
                <div
                  key={pack}
                  className="h-56 w-36 bg-gray-200 rounded-md relative overflow-hidden"
                  style={{
                    backgroundImage: `url(${getPackPreview(pack, model)})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <Tooltip content={packName[pack]}>
                    <div className="rounded-full h-6 text-center p-1 px-2 bg-blue-600 text-white text-xs bottom-1 left-1 absolute max-w-[70%] overflow-hidden whitespace-nowrap text-ellipsis">
                      {packName[pack]}
                    </div>
                  </Tooltip>
                  <Tooltip content={t("loras.review.photo_count_tooltip")}>
                    <div className="rounded-full w-6 h-6 text-center p-1 bg-blue-600 text-white text-xs bottom-1 right-1 absolute">
                      {lora.technology === "images" ? "x1" : "x4"}
                    </div>
                  </Tooltip>
                </div>
              ))}
            </div>
          </EnhancedScrollArea>

          <Button
            className="hue-animation"
            onClick={() => {
              if (lora?.technology === "images" && credits <= 150) {
                posthog.capture("click_free_try_pack", {});
              }
              defaultPacks.forEach((pack) => {
                create({
                  mode: "pack",
                  size: "portrait",
                  prompt: "",
                  model_id: lora.id,
                  pack_id: pack,
                  project_id: undefined,
                  count: lora.technology === "images" ? 1 : 4,
                  from_image: undefined,
                  similarity: 0,
                });
              });
              navigate(`/${site}/camera/new?mode=classic&model=${lora.id}`);
            }}
          >
            {t("loras.review.generate_button")}{" "}
            {!hideCosts && (
              <CoinsVal
                val={
                  defaultPacks.length *
                  (lora.technology === "images" ? 1 : 4) *
                  getPhotoCost("classic", lora)
                }
              />
            )}
          </Button>
          <Link
            href="#"
            className="mt-2 mb-6 block w-max"
            onClick={() =>
              navigate(`/${site}/templates?mode=pack&model=${lora.id}`)
            }
          >
            {hideCosts
              ? t("loras.review.skip_generation")
              : t("loras.review.select_custom_pack")}
          </Link>
        </div>
      )}

      {lora.status === "pending" && lora.technology !== "images" && (
        <div className="space-y-4">
          <Heading size="4" className="mb-4">
            {t("loras.review.pending")}
          </Heading>
          <Text className="block mb-6">{t("loras.review.pending_text")}</Text>
        </div>
      )}

      {lora.status === "failed" && lora.technology !== "images" && (
        <div className="space-y-4">
          <Heading size="4" className="mb-4">
            {t("loras.review.failed")}
          </Heading>
          <Text className="block mb-6">{t("loras.review.failed_text")}</Text>
        </div>
      )}
    </div>
  );
};

export const ModelReviewCard = ({ lora }: { lora: LorasType }) => {
  const { t } = useTranslation();

  const renderBadge = (
    fieldName: string,
    value: string | number | undefined
  ) => {
    if (!value) return null;
    const translatedValue = t([
      `loras.details.${fieldName}.${value}`,
      String(value),
    ]);
    return (
      <div className="m-1 rounded-lg px-3 py-1 bg-gray-200 dark:bg-gray-800 inline-block text-slate-500">
        {translatedValue}
      </div>
    );
  };

  if (lora.subtype === "avatar") {
    return (
      <div className="flex-wrap -m-1">
        {renderBadge("description", lora.details?.description)}
        {renderBadge("face", lora.details?.face)}
        <br />
        {renderBadge("gender", lora.details?.gender)}
        {renderBadge(
          "custom",
          t("loras.details.years_old", { age: lora.details.age })
        )}
        {renderBadge("body", lora.details?.body)}
        {renderBadge("skin", lora.details?.skin)}
      </div>
    );
  }

  if (lora.subtype === "animal") {
    return (
      <div className="flex-wrap -m-1">
        {renderBadge("description", lora.details?.description)}
        <br />
        {renderBadge("species", lora.details?.species)}
        {renderBadge("breed", lora.details?.breed)}
        {renderBadge("color", lora.details?.color)}
      </div>
    );
  }

  if (lora.subtype === "car") {
    return (
      <div className="flex-wrap -m-1">
        {renderBadge("description", lora.details?.description)}
        <br />
        {renderBadge("brand", lora.details?.brand)}
        {renderBadge("category", lora.details?.category)}
        {renderBadge("color", lora.details?.color)}
      </div>
    );
  }

  if (lora.subtype === "object") {
    return (
      <div className="flex-wrap -m-1">
        {renderBadge("description", lora.details?.description)}
        <br />
        {renderBadge("category", lora.details?.category)}
        {renderBadge("brand", lora.details?.brand)}
        {renderBadge("color", lora.details?.color)}
      </div>
    );
  }

  if (lora.subtype === "style") {
    return (
      <div className="flex-wrap -m-1">
        {renderBadge("description", lora.details?.description)}
        <br />
        {renderBadge("ratio", lora.details?.ratio)}
        {lora.details?.nsfw && renderBadge("custom", t("loras.details.nsfw"))}
      </div>
    );
  }

  return null;
};
